import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Fullscreen,
  FilterList,
  AttachMoney,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Line,
} from 'recharts';

interface CostAnalysisChartProps {
  data?: any[];
  loading?: boolean;
  selectedProject?: string;
}

const CostAnalysisChart: React.FC<CostAnalysisChartProps> = ({
  data,
  loading = false,
  selectedProject = 'all',
}) => {
  // Mock data for demonstration
  const mockCostData = [
    { category: 'Labor', budgeted: 450000, actual: 425000, variance: -25000 },
    { category: 'Materials', budgeted: 320000, actual: 345000, variance: 25000 },
    { category: 'Equipment', budgeted: 180000, actual: 175000, variance: -5000 },
    { category: 'Overhead', budgeted: 120000, actual: 115000, variance: -5000 },
    { category: 'Subcontractors', budgeted: 200000, actual: 210000, variance: 10000 },
  ];

  const mockTrendData = [
    { month: 'Jan', budget: 200000, actual: 195000, forecast: 198000 },
    { month: 'Feb', budget: 220000, actual: 225000, forecast: 222000 },
    { month: 'Mar', budget: 250000, actual: 245000, forecast: 248000 },
    { month: 'Apr', budget: 280000, actual: 285000, forecast: 282000 },
    { month: 'May', budget: 300000, actual: 295000, forecast: 298000 },
    { month: 'Jun', budget: 320000, actual: 315000, forecast: 318000 },
  ];

  const costData = data || mockCostData;
  const trendData = mockTrendData;

  const totalBudgeted = costData.reduce((sum, item) => sum + item.budgeted, 0);
  const totalActual = costData.reduce((sum, item) => sum + item.actual, 0);
  const totalVariance = totalActual - totalBudgeted;
  const variancePercentage = (totalVariance / totalBudgeted) * 100;

  const getVarianceColor = (variance: number) => {
    if (variance > 0) return 'error';
    if (variance < -10000) return 'success';
    return 'warning';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Cost Analysis
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Cost Overview */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Cost Overview
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Budget
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    ${(totalBudgeted / 1000).toFixed(0)}K
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Actual Spend
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    ${(totalActual / 1000).toFixed(0)}K
                  </Typography>
                </Box>
                <Chip
                  icon={totalVariance >= 0 ? <TrendingUp /> : <TrendingDown />}
                  label={`${totalVariance >= 0 ? '+' : ''}${variancePercentage.toFixed(1)}%`}
                  color={getVarianceColor(totalVariance)}
                  size="small"
                />
              </Box>
            </Box>
            <Box>
              <IconButton size="small">
                <FilterList />
              </IconButton>
              <IconButton size="small">
                <Fullscreen />
              </IconButton>
            </Box>
          </Box>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={costData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="category" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#666' }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#666' }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip 
                formatter={(value: number) => [`$${(value / 1000).toFixed(0)}K`, '']}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              />
              <Legend />
              <Bar dataKey="budgeted" fill="#1976d2" name="Budgeted" radius={[4, 4, 0, 0]} />
              <Bar dataKey="actual" fill="#388e3c" name="Actual" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Cost Trend */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Cost Trend Analysis
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="month" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#666' }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#666' }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip 
                formatter={(value: number) => [`$${(value / 1000).toFixed(0)}K`, '']}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              />
              <Legend />
              <Bar dataKey="budget" fill="#1976d2" name="Budget" opacity={0.7} />
              <Bar dataKey="actual" fill="#388e3c" name="Actual" opacity={0.7} />
              <Line 
                type="monotone" 
                dataKey="forecast" 
                stroke="#f57c00" 
                strokeWidth={3}
                strokeDasharray="5 5"
                name="Forecast"
                dot={{ fill: '#f57c00', strokeWidth: 2, r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Cost Breakdown Cards */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {costData.slice(0, 3).map((item, index) => {
          const utilizationPercentage = (item.actual / item.budgeted) * 100;
          return (
            <Card key={index} sx={{ flex: 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AttachMoney color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {item.category}
                  </Typography>
                </Box>
                <Typography variant="h6" gutterBottom>
                  ${(item.actual / 1000).toFixed(0)}K
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(utilizationPercentage, 100)} 
                  color={utilizationPercentage > 100 ? 'error' : utilizationPercentage > 90 ? 'warning' : 'success'}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {utilizationPercentage.toFixed(1)}% of budget
                </Typography>
                <Typography 
                  variant="body2" 
                  color={item.variance > 0 ? 'error.main' : 'success.main'}
                >
                  {item.variance > 0 ? '+' : ''}${(item.variance / 1000).toFixed(0)}K variance
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default CostAnalysisChart;